import { Footer, notify, RoutesInside, SidebarContainer, useFinalRoutes } from "@general-backoffice/core";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Button, Card, Container } from "reactstrap";

import useMyBillingData from "../../components/billingData/hooks/useMyBillingData";
import SessionHeader from "../../components/layout/SessionHeader";
import ExtendedIcon from "../../components/shared/ExtendedIcon";
import LanguageSelectorModal from "../../components/shared/LanguageSelectorModal";
import TaxProfileMigrationBanner from "../../components/taxProfile/TaxProfileMigrationBanner";
import WarningsList from "../../components/warnings/WarningsList";
import { isPartnerType } from "../../constants/partnerTypes";
import { AdminViewingUserProvider } from "../../contexts/AdminViewingUserContext";
import useFooterContent from "../../hooks/useFooterContent";
import useTaxProfileMigrationStatus from "../../hooks/useTaxProfileMigrationStatus";
import DashboardNotificationsWrapper, {
  DashboardNotificationsContext,
} from "../../hooks/wrappers/DashboardNotificationsWrapper";
import android from "../../media/androidLogo.png";
import ios from "../../media/iosLogo.png";
import { activateExtendedMode, logout } from "../../redux/reducers/session/actions";
import type { RootState } from "../../redux/reducers/session/state";
import logo from "./../../media/branding/logo.png";

const WrappedDashboard: React.FC = () => (
  <AdminViewingUserProvider>
    <DashboardNotificationsWrapper>
      <Dashboard />
    </DashboardNotificationsWrapper>
  </AdminViewingUserProvider>
);

const Dashboard: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "layout" });

  const navigate = useNavigate();
  const routes = useFinalRoutes();
  const { user, extendedMode } = useSelector((state: RootState) => state.sessionState);
  const [isOpenSidebar, setOpenSidebar] = useState(() => window.matchMedia("(min-width: 1200px)").matches);
  // Whether the user has overridden the breakpoint default by toggling.
  const sidebarTouched = useRef(false);
  const [isOpenLanguage, setOpenLanguage] = useState(false);
  const dispatch = useDispatch();
  const taxProfileMigration = useTaxProfileMigrationStatus();

  // Prefetch billing data when migration banner is shown, so it's cached for the billing page
  const shouldPrefetchBillingData =
    taxProfileMigration?.state &&
    taxProfileMigration.state.inGracePeriod &&
    !taxProfileMigration.state.taxProfileComplete;
  // Pass 1 (arbitrary StrapiId) to trigger autoload, or undefined to skip
  useMyBillingData(shouldPrefetchBillingData ? 1 : undefined);

  // Without this the sidebar state was decided once at mount: resizing a window
  // or rotating a tablet left it expanded over the content, or collapsed with
  // no way to reopen it on desktop.
  useEffect(() => {
    // 1200px is where the stylesheet reserves space for the sidebar; a
    // lower value paints it over the content.
    const query = window.matchMedia("(min-width: 1200px)");
    const onChange = (e: MediaQueryListEvent | MediaQueryList) => {
      if (sidebarTouched.current) return;
      setOpenSidebar(e.matches);
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const toggleSidebar = useCallback((open: boolean) => {
    sidebarTouched.current = true;
    setOpenSidebar(open);
  }, []);

  const { notifications, markAllAsRead, markEventRead, unreadCount } = useContext(DashboardNotificationsContext);
  const [countToActivateExtendedMode, setCountToActivateExtendedMode] = useState(0);
  const footerContent = useFooterContent();

  useEffect(() => {
    if (!extendedMode && countToActivateExtendedMode >= 10) {
      notify(
        <div>
          <ExtendedIcon className="animation-zoom-5 px-3" />
          {t("extendedModeActivated")}
        </div>
      );
      dispatch(activateExtendedMode());
    }
  }, [countToActivateExtendedMode, extendedMode, dispatch, t]);

  const notificationsCount = notifications?.length || 0;
  const notificationsHeader =
    notificationsCount > 0 ? (
      <div className="d-flex justify-content-between align-items-center w-100">
        <span>{t("notificationsHeaderWithCount", { count: notificationsCount })}</span>
        <Button size="sm" color="link" className="p-0 ml-2" onClick={markAllAsRead}>
          {t("markAllRead")}
        </Button>
      </div>
    ) : (
      t("notificationsHeaderEmpty")
    );

  const sessionOptions = [
    ...(user?.role?.name === "Authenticated"
      ? [
          {
            title: t("dropdown.managePayments"),
            icon: "fas fa-credit-card",
            onClick: () => navigate("/manage-payments"),
          },
          ...(isPartnerType(user?.referrerType ?? "")
            ? [
                {
                  title: t("dropdown.referrals"),
                  icon: "fas fa-bullhorn",
                  onClick: () => navigate("/referrals"),
                },
              ]
            : []),
        ]
      : []),
    {
      title: t("dropdown.language"),
      icon: "fas fa-globe",
      onClick: () => setOpenLanguage(true),
    },
    {
      title: t("dropdown.logout"),
      onClick: () => dispatch(logout()),
      icon: "fas fa-sign-out-alt",
      useDivider: true,
    },
  ];

  const userName = user?.name || user?.email?.split("@")[0];

  return (
    <React.Fragment>

      {user?.role?.name === "Not verified" && (
        <div className="w-100 bg-danger text-white text-center p-4 font-weight-bold">
          {t("pendingValidationWarning")}
        </div>
      )}

      <LanguageSelectorModal isOpen={isOpenLanguage} setOpen={setOpenLanguage} />

      <Container fluid className="p-0 m-0 min-vh-100">
        <SidebarContainer
          routes={routes}
          isOpen={isOpenSidebar}
          setOpen={toggleSidebar}
          logoSrc={logo}
          onClickLogo={() => setCountToActivateExtendedMode((c) => c + 1)}
          underSidenav={
            <>
              <TrustpilotBanner />
              <AppsBanner />

              <SchedulingBanner />
            </>
          }
        >
          <div style={{ minHeight: "100dvh" }} className="d-flex flex-column justify-content-between">
            <div>
              <SessionHeader
                isOpen={isOpenSidebar}
                setOpen={toggleSidebar}
                sessionName={
                  <div className="mb--2">
                    <div style={{ lineHeight: ".7rem" }}>
                      {userName}
                      {user?.role?.name === "Admin" && <i className="fas fa-wrench ml-1" />}
                    </div>
                    <small>{user?.email ?? ""}</small>
                  </div>
                }
                notifications={notifications || []}
                notificationsCount={unreadCount}
                onMarkEventRead={markEventRead}
                isDark={false}
                color="secondary"
                avatarSrc={`https://ui-avatars.com/api/?background=random&name=${userName}`}
                sessionOptions={sessionOptions}
                sessionOptionsHeader={t("dropdown.sessionOptionsHeader")}
                notificationsHeader={notificationsHeader}
              />

              <WarningsList />

              <div className="px-3 px-lg-4">
                <TaxProfileMigrationBanner migrationStatus={taxProfileMigration} />
              </div>

              <RoutesInside />
            </div>

            <Footer.Session brand="INVESTHINKS" content={footerContent} />
          </div>
        </SidebarContainer>
      </Container>
    </React.Fragment>
  );
};

// Extend global Window interface for Trustpilot widget
declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (el: HTMLElement | null, reload: boolean) => void;
    };
  }
}

const TrustpilotBanner: React.FC = () => {
  const { i18n } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.Trustpilot) {
      window.Trustpilot.loadFromElement(ref.current, true);
    }
  }, [i18n?.language]);

  return (
    <div className="w-100 mx--2 overflow-hidden">
      <div style={{ minWidth: 215 }}>
        <div
          ref={ref}
          className="trustpilot-widget"
          data-locale={i18n?.language}
          data-template-id="56278e9abfbbba0bdcd568bc"
          data-businessunit-id="664cbfaa17b01faefa813021"
          data-style-height="52px"
          data-style-width="150%"
          style={{ transform: "translateX(-26%)" }}
        >
          <a href="https://es.trustpilot.com/review/investhinks.com" target="_blank" rel="noopener noreferrer">
            Trustpilot
          </a>
        </div>
      </div>
    </div>
  );
};

const AppsBanner: React.FC = () => (
  <Card className="bg-gradient-default text-white rounded mt-2 mx--1 overflow-hidden">
    <div className="px-3 pt-3 text-center mx-auto" style={{ minWidth: 207 }}>
      <p>
        <Trans ns="dashboard" i18nKey="layout.appsBanner" components={[<b key="0" className="h2 text-primary" />]} />
      </p>
      <div className="flex-center pb-3 mx-1" style={{ gap: "2rem" }}>
        <a
          href="https://apps.apple.com/es/app/investhinks/id6529520559"
          target="_blank"
          rel="noreferrer"
          className="w-50"
        >
          <img src={ios} alt="apple store logo" width="100%" />
        </a>
        <a
          href="https://play.google.com/store/apps/details?id=com.investhinks.mobile&pcampaignid=web_share"
          target="_blank"
          rel="noreferrer"
          className="w-50"
        >
          <img src={android} alt="play store logo" width="100%" />
        </a>
      </div>
    </div>
  </Card>
);

const schedulingLink = "https://cal.com/investhinks";
const SchedulingBanner: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "layout" });
  return (
    <Card className="bg-gradient-success text-white rounded mt-2 mx--1 overflow-hidden">
      <div className="p-3 text-center mx-auto" style={{ minWidth: 207 }}>
        <div className="h1 text-white mb--1">
          <i className="fas fa-calendar-alt" />
        </div>
        <p>
          <Trans
            ns="dashboard"
            i18nKey="layout.schedulingBanner"
            components={[<span key="0" className="h3 text-white" />]}
          />
        </p>
        <div>
          <a href={schedulingLink} target="_blank" rel="noopener noreferrer" className="btn btn-default flex-center">
            <span>{t("schedulingButton")}</span>
          </a>
        </div>
      </div>
    </Card>
  );
};

export default WrappedDashboard;
